import { useEffect, useState } from 'react';

// Observe the entire expanded workout, not the heading. A viewport root also
// clips against nested scroll containers; no scroll listener or polling needed.
export default function useWorkoutShortcutVisibility(activityId, foreground) {
  const [visible, setVisible] = useState(null);
  useEffect(() => {
    setVisible(null);
    if (!activityId || !foreground || typeof IntersectionObserver === 'undefined') return undefined;
    const feed = document.querySelector('.activity-list');
    const topBar = document.querySelector('.top-bar');
    let target;
    let observer;
    let margin;
    let disposed = false;
    const sync = () => {
      const next = document.getElementById(`activity-${activityId}`)?.querySelector('.todo-content') || null;
      const inset = Math.max(0, topBar?.getBoundingClientRect().bottom || 0);
      const nextMargin = `-${Math.ceil(inset)}px 0px 0px 0px`;
      if (next === target && margin === nextMargin) return;
      target = next;
      margin = nextMargin;
      observer?.disconnect();
      setVisible(false);
      if (!target) return;
      const observed = target;
      const current = new IntersectionObserver(entries => {
        if (disposed || observer !== current || target !== observed) return;
        const entry = entries.find(item => item.target === observed);
        if (entry) setVisible(entry.isIntersecting && entry.intersectionRect.height > 0 && entry.intersectionRect.width > 0);
      }, { root: null, rootMargin: margin, threshold: 0 });
      observer = current;
      observer.observe(target);
    };
    sync();
    // Feed filters, expansion, and async mounting can replace the target. Ignore
    // unrelated subtree changes without restarting an existing observation.
    const mutations = new MutationObserver(sync);
    if (feed) mutations.observe(feed, { childList: true, subtree: true });
    // Reduced motion has no transitionend; class changes still move the bar.
    if (topBar) mutations.observe(topBar, { attributes: true, attributeFilter: ['class'] });
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    if (topBar) resize?.observe(topBar);
    topBar?.addEventListener('transitionend', sync);
    window.addEventListener('resize', sync);
    return () => {
      disposed = true;
      observer?.disconnect();
      mutations.disconnect();
      resize?.disconnect();
      topBar?.removeEventListener('transitionend', sync);
      window.removeEventListener('resize', sync);
    };
  }, [activityId, foreground]);
  return visible;
}
